import { Dialog } from "@headlessui/react"
import React, { FC, useEffect, useState } from "react"
import { X } from "heroicons-react"
import { User, UserAdvance } from "../../../../../types"
import {
  currencyOptions,
  errorFilledToastOptions,
  successFilledToastOptions,
  USER_ADVANCE_TYPES
} from "../../../../../constants"
import { users } from "../../../../../apis"
import { mapListToCamelCase, mapToSnakeCase } from "../../../../../utility"
import { KButton, KDropdown, KInput, KSelectDate, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { toast } from "react-toastify"

type AdminUserAdvanceModalProps = {
  open: boolean
  onClose: () => void
  userId: number | undefined
  user: User | null
  onChangeCurrencyClick: () => void
  setSelectedCurrencyId: (selectedCurrencyId: number | undefined) => void
}

const advanceTypeOptions = [
  { label: "Direct Payment", value: 1, value2: USER_ADVANCE_TYPES.DIRECT_PAYMENT },
  { label: "Marketing", value: 2, value2: USER_ADVANCE_TYPES.MARKETING }
]

const AdminUserAdvanceModal: FC<AdminUserAdvanceModalProps> = (props) => {
  const { open } = props
  const [loading, setLoading] = useState(false)

  const [userAdvanceList, setUserAdvanceList] = useState<UserAdvance[]>([])
  const [userAdvanceToCreate, setUserAdvanceToCreate] = useState<UserAdvance>()
  const [addOpen, setAddOpen] = useState(false)
  const [existingCurrencyId, setExistingCurrencyId] = useState<number | undefined>(undefined)
  const [openUserAdvanceExist, setOpenUserAdvanceExist] = useState<boolean>(true)

  const fetchUserAdvanceList = () => {
    users(`/fetch_user_advance/?user_id=${props.userId}`, "GET").then((resp) => {
      const advanceList: UserAdvance[] = mapListToCamelCase(resp.data.results)
      setUserAdvanceList(advanceList)

      const openAdvance = advanceList.some((advance: UserAdvance) => {
        const advanceTotal = advance?.advanceTotal ?? 0
        const paidTotal = advance?.paidTotal ?? 0

        if (Number(advanceTotal.toFixed(7)) - Number(paidTotal.toFixed(7)) > 0) {
          return true
        }
        return false
      })

      setOpenUserAdvanceExist(openAdvance)

      if (resp.data.results.length > 0) {
        const currencyId = resp.data.results[0].currency_id
        setExistingCurrencyId(currencyId)
        props.setSelectedCurrencyId(currencyId)
        setUserAdvanceToCreate({ currency: currencyId, originalCurrency: currencyId })
      }
    })
  }

  useEffect(() => {
    if (!(props.userId && open)) {
      setUserAdvanceList([])
      return
    }

    fetchUserAdvanceList()
  }, [props.userId, open])

  const onCloseModal = () => {
    setUserAdvanceList([])
    setUserAdvanceToCreate(undefined)
    setExistingCurrencyId(undefined)
    props.setSelectedCurrencyId(undefined)
    props.onClose()
    setAddOpen(false)
  }

  const saveUserAdvance = () => {
    /* All user advances currency for a user will be the same */
    setLoading(true)
    users(`/user_advance/`, "POST", { ...mapToSnakeCase(userAdvanceToCreate), user: props.userId })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Created Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
          setUserAdvanceToCreate(undefined)
        } else {
          toast.error("Error saving User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error saving User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const updateUserAdvance = (userAdvance: UserAdvance, endDate?: Date) => {
    const a = userAdvance?.endDate?.substring(0, 10)
    const b = endDate?.toISOString().substring(0, 10)

    if (a === b) {
      return
    }
    endDate?.setHours(9)

    setLoading(true)
    users(`/update_user_advance/`, "PUT", { id: userAdvance.id, end_date: endDate })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Updated Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
        } else {
          toast.error("Error updating User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error updating User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const deleteUserAdvance = (userAdvanceId: number) => {
    if (userAdvanceId === 0) {
      setAddOpen(false)
      return
    }

    if (!window.confirm("Are you sure?")) {
      return
    }

    setLoading(true)
    users(`/remove_user_advance/?id=${userAdvanceId}`, "DELETE")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Removed Successfully.", successFilledToastOptions)
        } else {
          toast.error("Error removing User Advance!.", errorFilledToastOptions)
        }
        fetchUserAdvanceList()
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error removing User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const updateAdvanceEndDate = (selected: Date) => {
    console.log(selected)
  }

  return (
    <Dialog open={open} onClose={() => onCloseModal()} className="relative">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-white elev-b-sm rounded-lg overflow-hidden" style={{ padding: 42 }}>
          <div className="flex flex-row items-center justify-between gap-1 mb-2">
            <div className="flex items-center gap-1">
              <KSpan text="User:" fontWeight={500} color="#000" />
              <KSpan text={`${props?.user?.firstName} ${props?.user?.lastName}`} color="#000" />
            </div>
            {existingCurrencyId &&
              (openUserAdvanceExist ? (
                <KTooltip
                  backgroundColor="#000"
                  padding="0px 4px"
                  height="auto"
                  arrowColor="#000"
                  position="left"
                  content={
                    <div className=" w-[220px] flex items-center py-2 px-2">
                      <KSpan
                        text={
                          "You cannot change the advance currency while there are advances that are not fully settled, i.e., those with a remaining balance."
                        }
                        fontSize={12}
                        lineHeight="16px"
                        color="#FFF"
                      />
                    </div>
                  }
                >
                  <div className="flex items-center">
                    <KButton text="Change Currency" disabled={true} onClick={props.onChangeCurrencyClick} />
                  </div>
                </KTooltip>
              ) : (
                <div className="flex items-center">
                  <KButton
                    text="Change Currency"
                    disabled={openUserAdvanceExist}
                    onClick={props.onChangeCurrencyClick}
                  />
                </div>
              ))}
          </div>{" "}
          <div className="mb-4">
            <KSpan text="You can update Advance Paid anytime. Select to edit. Advance will still be charged using Advance Approved date. Advance Paid is only for analytics" />
          </div>
          <div className={`flex justify-end`}>
            <X className="h-4 cursor-pointer mt-1" style={{ position: "absolute" }} onClick={() => onCloseModal()} />
          </div>
          <div className="flex mb-4 items-center">
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Advance Start (Activity Period)"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Advance Paid"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Advance Total"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Paid Total"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Type"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 100 }}>
              <KTitleSpan text={"Currency"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 100 }}>
              <KTitleSpan text={"Original Currency"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 140 }}>
              <KTitleSpan text={"Created At"} fontSize={16} lineHeight="auto" />
            </div>
            <div style={{ width: 100 }}>
              <KTitleSpan text={"Action"} fontSize={16} lineHeight="auto" />
            </div>
          </div>
          <div className="flex flex-col gap-0 max-h-[50vh] overflow-auto">
            {userAdvanceList.map((userAdvance: UserAdvance, index: number) => {
              return (
                <div
                  key={`user-advance-${index}`}
                  className="flex mt-2 pb-2 items-center"
                  style={{ width: "100%", borderBottom: "1px dashed grey" }}
                >
                  <div style={{ width: 140 }}>
                    <KSpan text={userAdvance?.startDate?.substring(0, 10) || "-"} />
                  </div>
                  <div className="flex gap-2 items-center" style={{ width: 140 }}>
                    <KSpan text={userAdvance?.endDate?.substring(0, 10) || "-"} />
                    <KSelectDate
                      icon="/analytics_icons/calendar.svg"
                      boxShadow={"none"}
                      hideBody={true}
                      value={userAdvance?.endDate ? new Date(userAdvance.endDate) : undefined}
                      onChange={(value) => updateUserAdvance(userAdvance, value)}
                    />
                  </div>
                  <div style={{ width: 140 }}>
                    <KSpan text={userAdvance?.advanceTotal?.toFixed(2) || "-"} />
                  </div>
                  <div style={{ width: 140 }}>
                    <KSpan text={userAdvance?.paidTotal?.toFixed(2) || "-"} />
                  </div>
                  <div style={{ width: 140 }}>
                    <KSpan
                      text={
                        advanceTypeOptions.find((option) => option.value2 === userAdvance?.advanceType)?.label || "-"
                      }
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <KSpan
                      text={currencyOptions.find((option) => option.value === userAdvance?.currencyId)?.label || "-"}
                    />
                  </div>
                  <div style={{ width: 100 }}>
                    <KSpan
                      text={
                        currencyOptions.find((option) => option.value === userAdvance?.originalCurrencyId)?.label || "-"
                      }
                    />
                  </div>
                  <div style={{ width: 140 }}>
                    <KSpan text={userAdvance?.created?.substring(0, 10) || "-"} />
                  </div>
                  <div style={{ width: 100 }}>
                    <KButton
                      dataTestId="delete-user-advance"
                      icon={"/account_icons/trash_red.svg"}
                      padding="8px"
                      height="36px"
                      width="36px"
                      background="#FFF"
                      hoverBackground="#FFE6E8"
                      activeBackground="#FFF"
                      border="1px solid #FF5865"
                      hoverBorder="1px solid transparent"
                      activeBorder="1px solid #FF5865"
                      onClick={() => deleteUserAdvance(userAdvance?.id || 0)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
          {addOpen && (
            <div className="flex mt-8" style={{ width: "100%" }}>
              <div className="pr-2" style={{ width: 140 }}>
                <KInput
                  dataTestId="user-advance-start-date"
                  placeholder="20240124"
                  value={userAdvanceToCreate?.startDate || ""}
                  onChange={(value) => setUserAdvanceToCreate({ ...userAdvanceToCreate, startDate: value })}
                />
              </div>
              <div className="pr-2" style={{ width: 140 }}>
                <KInput
                  dataTestId="user-advance-end-date"
                  placeholder="20240124"
                  value={userAdvanceToCreate?.endDate || ""}
                  onChange={(value) => setUserAdvanceToCreate({ ...userAdvanceToCreate, endDate: value })}
                />
              </div>
              <div className="pr-2" style={{ width: 140 }}>
                <KInput
                  dataTestId="user-advance-total-input"
                  placeholder="2000"
                  value={userAdvanceToCreate?.advanceTotal?.toString() || ""}
                  onChange={(value) => {
                    if (value) {
                      setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceTotal: parseInt(value) })
                    } else {
                      setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceTotal: 0 })
                    }
                  }}
                />
              </div>
              <div style={{ width: 140 }}></div>
              <div className="pr-2" style={{ width: 140 }}>
                <KDropdown
                  dataTestId="user-advance-type-dropdown"
                  key={`${userAdvanceToCreate?.advanceType}`}
                  placeholder="Direct Paym..."
                  menuWidth={160}
                  options={advanceTypeOptions}
                  menuLeftMargin={-32}
                  selected={advanceTypeOptions.find((option) => option.value2 === userAdvanceToCreate?.advanceType)}
                  onSelect={(selected: any) =>
                    setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceType: selected.value2 })
                  }
                  rightIcon="/admin_icons/caret-down.svg"
                  enableIndicator
                  isEllipsis
                />
              </div>

              <div className="pr-2 flex items-center" style={{ width: 100 }}>
                <div className="absolute">
                  {!existingCurrencyId ? (
                    <KDropdown
                      dataTestId="user-advance-currency-dropdown"
                      placeholder="TRY"
                      width={80}
                      menuWidth={80}
                      options={currencyOptions}
                      selected={currencyOptions.find((option) => option.value === userAdvanceToCreate?.currency)}
                      onSelect={(selected: any) =>
                        setUserAdvanceToCreate({
                          ...userAdvanceToCreate,
                          currency: selected.value,
                          originalCurrency: selected.value
                        })
                      }
                      rightIcon="/admin_icons/caret-down.svg"
                      enableIndicator
                    />
                  ) : (
                    <KTooltip
                      backgroundColor="#000"
                      padding="0px 4px"
                      height="auto"
                      arrowColor="#000"
                      position="left"
                      content={
                        <div className=" w-[220px] flex items-center py-2 px-2">
                          <KSpan
                            text={
                              "You cannot select a currency different from the existing advances. If you need to make such a selection, please click the 'Change Currency' button to update the currency of the advances."
                            }
                            fontSize={12}
                            lineHeight="16px"
                            color="#FFF"
                          />
                        </div>
                      }
                    >
                      <KSpan
                        text={currencyOptions.find((option) => option.value === existingCurrencyId)?.label || "-"}
                      />
                    </KTooltip>
                  )}
                </div>
              </div>
              <div style={{ width: 100 }} />
              <div style={{ width: 140 }} />
              <div style={{ width: 100 }} className="items-center flex">
                <KButton
                  dataTestId="delete-user-advance-button"
                  icon={"/account_icons/trash_red.svg"}
                  padding="8px"
                  height="36px"
                  width="36px"
                  background="#FFF"
                  hoverBackground="#FFE6E8"
                  activeBackground="#FFF"
                  border="1px solid #FF5865"
                  hoverBorder="1px solid transparent"
                  activeBorder="1px solid #FF5865"
                  onClick={() => deleteUserAdvance(0)}
                />
              </div>
            </div>
          )}
          <div className="mt-8">
            <KButton
              dataTestId="add-user-advance-button"
              background={addOpen ? "black" : "transparent"}
              textColor={addOpen ? "white" : "black"}
              shadowDisabled={true}
              width="auto"
              text={addOpen ? "Save Advance" : "+ Add New"}
              onClick={() => (addOpen ? saveUserAdvance() : setAddOpen(true))}
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default AdminUserAdvanceModal
